import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useItems } from '../hooks/useItems';
import { useTags } from '../hooks/useTags';
import { itemsApi } from '../api/items';
import AuthorFilter from '../components/manga/AuthorFilter';
import MangaCard from '../components/manga/MangaCard';
import SeriesCard from '../components/manga/SeriesCard';
import { getGojuonCategory } from '../utils/gojuon';
import { groupIntoSeries } from '../utils/seriesUtils';
import {
  TAG_GROUPS,
  type TagGroupKey,
  getTagGroup,
  getTagLabel,
  getTagFilterClass,
} from '../utils/tagUtils';
import type { Tag } from '../types';

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    const raw = searchParams.get('tags') ?? '';
    return raw
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  });
  const [selectedAuthorCategory, setSelectedAuthorCategory] = useState<string>(
    () => searchParams.get('author') ?? ''
  );
  const [selectedRating, setSelectedRating] = useState<number | null>(() => {
    const rating = searchParams.get('rating');
    return rating !== null ? parseInt(rating, 10) : null;
  });
  const [showInactiveOnly, setShowInactiveOnly] = useState(
    () => searchParams.get('inactive') === '1'
  );
  const [sortOrder, setSortOrder] = useState<'created_at' | 'title' | 'rating'>(() => {
    const s = searchParams.get('sort');
    if (s === 'title' || s === 'rating') return s;
    return 'created_at';
  });
  const [keyword, setKeyword] = useState<string>(
    () => searchParams.get('keyword') ?? ''
  );

  const handleSortCycle = () => {
    setSortOrder((prev) => {
      if (prev === 'created_at') return 'title';
      if (prev === 'title') return 'rating';
      return 'created_at';
    });
  };

  // 常に全件取得してシリーズグループ化を正確に行う
  const firstTag = selectedTags[0];
  const { data, isLoading, error } = useItems({
    tag: firstTag,
    limit: 999,
    include_inactive: showInactiveOnly ? true : undefined,
  });
  const { data: tagsData } = useTags();

  const groupedTags = useMemo(() => {
    const groups: Record<TagGroupKey, Tag[]> = {
      style: [],
      mood: [],
      other: [],
    };

    tagsData?.tags.forEach((tag) => {
      // Only include tags with use_count > 0 (hide unused tags)
      if ((tag.use_count ?? 0) <= 0) return;
      groups[getTagGroup(tag.name)].push(tag);
    });

    (Object.keys(groups) as TagGroupKey[]).forEach((key) => {
      groups[key].sort((a, b) => a.name.localeCompare(b.name));
    });

    return groups;
  }, [tagsData]);

  const displayEntries = useMemo(() => {
    if (!data?.items) return [];

    let filtered = data.items;

    // キーワードフィルタ
    // DBデータはmacOSファイルシステム由来のNFD、入力はNFCのため両辺をNFCに統一
    if (keyword.trim()) {
      const kw = keyword.trim().normalize('NFC').toLowerCase();
      filtered = filtered.filter((item) =>
        item.title.normalize('NFC').toLowerCase().includes(kw) ||
        item.author.normalize('NFC').toLowerCase().includes(kw) ||
        (item.alias?.normalize('NFC').toLowerCase().includes(kw) ?? false)
      );
    }

    if (showInactiveOnly) {
      filtered = filtered.filter((item) => item.is_inactive === true);
    }

    // 50音順フィルタ
    if (selectedAuthorCategory) {
      if (selectedAuthorCategory === 'other') {
        filtered = filtered.filter((item) => {
          const category = getGojuonCategory(item.author_reading);
          return category === 'other';
        });
      } else {
        filtered = filtered.filter((item) => {
          const category = getGojuonCategory(item.author_reading);
          return category === selectedAuthorCategory;
        });
      }
    }

    // タグフィルタ (クライアントサイド)
    // 最初のタグは既にAPIで絞り込み済みなので、2個目以降のタグのみをフィルタ
    const remainingTags = selectedTags.slice(1);
    if (remainingTags.length > 0) {
      filtered = filtered.filter((item) => {
        if (!item.tags || item.tags.length === 0) return false;
        const itemTagNames = new Set(item.tags.map((tag) => tag.name));
        return remainingTags.every((tag) => itemTagNames.has(tag));
      });
    }

    // シリーズグループ化
    const entries = groupIntoSeries(filtered);

    // レーティングフィルタ: グループ化後に適用
    // シリーズは「いずれかの巻が条件を満たす」なら表示
    const filtered2 =
      selectedRating !== null
        ? entries.filter((entry) => {
            if (entry.type === 'standalone') {
              return entry.item.rating === selectedRating;
            }
            return entry.volumes.some((v) => v.rating === selectedRating);
          })
        : entries;

    // ソート
    return [...filtered2].sort((a, b) => {
      if (sortOrder === 'title') {
        const titleA = a.type === 'series' ? a.baseTitle : a.item.title;
        const titleB = b.type === 'series' ? b.baseTitle : b.item.title;
        return titleA.localeCompare(titleB, 'ja');
      }
      if (sortOrder === 'rating') {
        const ratingA = a.type === 'series' ? a.representativeItem.rating : a.item.rating;
        const ratingB = b.type === 'series' ? b.representativeItem.rating : b.item.rating;
        return ratingB - ratingA;
      }
      // created_at: 新しい順（シリーズは最新巻の追加日を使用）
      const dateA =
        a.type === 'series'
          ? Math.max(...a.volumes.map((v) => new Date(v.created_at).getTime()))
          : new Date(a.item.created_at).getTime();
      const dateB =
        b.type === 'series'
          ? Math.max(...b.volumes.map((v) => new Date(v.created_at).getTime()))
          : new Date(b.item.created_at).getTime();
      return dateB - dateA;
    });
  }, [data?.items, selectedTags, selectedAuthorCategory, selectedRating, showInactiveOnly, sortOrder, keyword]);

  const toggleTag = (tagName: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagName)
        ? prev.filter((tag) => tag !== tagName)
        : [...prev, tagName]
    );
  };

  const handleClearFilters = () => {
    setSelectedTags([]);
    setSelectedAuthorCategory('');
    setSelectedRating(null);
    setShowInactiveOnly(false);
    setKeyword('');
    setSearchParams({}, { replace: true });
  };

  const handleEnterSelectMode = () => {
    setIsSelectMode(true);
    setSelectedIds(new Set());
  };

  const handleCancelSelectMode = () => {
    setIsSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleToggleId = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleIds = (ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const confirmed = window.confirm(
      `選択した ${selectedIds.size} 件の作品を完全に削除しますか？\nこの操作は元に戻せません。`
    );
    if (!confirmed) return;

    await Promise.allSettled(
      [...selectedIds].map((id) => itemsApi.deleteItem(id))
    );

    setIsSelectMode(false);
    setSelectedIds(new Set());
    queryClient.invalidateQueries({ queryKey: ['items'] });
  };

  const handleRatingChange = (rating: number | null) => {
    setSelectedRating((prev) => (prev === rating ? null : rating));
    // レーティングフィルタと作者フィルタは排他的
    if (rating !== null) {
      setSelectedAuthorCategory('');
    }
  };

  const handleAuthorCategoryChange = (category: string) => {
    setSelectedAuthorCategory((prev) => (prev === category ? '' : category));
    // レーティングフィルタと作者フィルタは排他的
    if (category) {
      setSelectedRating(null);
    }
  };

  const SCROLL_KEY = 'manga-list-scroll';

  const handleCardClick = (id: string) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    navigate(`/manga/${id}${location.search}`);
  };

  // ビューア画面から戻った時にスクロール位置を復元
  useEffect(() => {
    if (isLoading || displayEntries.length === 0) return;
    const savedY = sessionStorage.getItem(SCROLL_KEY);
    if (!savedY) return;
    sessionStorage.removeItem(SCROLL_KEY);
    window.scrollTo({ top: parseInt(savedY, 10), behavior: 'instant' });
  }, [isLoading, displayEntries]);

  useEffect(() => {
    const nextParams: Record<string, string> = {};
    if (keyword) {
      nextParams.keyword = keyword;
    }
    if (selectedTags.length > 0) {
      nextParams.tags = selectedTags.join(',');
    }
    if (selectedAuthorCategory) {
      nextParams.author = selectedAuthorCategory;
    }
    if (selectedRating !== null) {
      nextParams.rating = String(selectedRating);
    }
    if (showInactiveOnly) {
      nextParams.inactive = '1';
    }
    if (sortOrder !== 'created_at') {
      nextParams.sort = sortOrder;
    }
    setSearchParams(nextParams, { replace: true });
  }, [keyword, selectedTags, selectedAuthorCategory, selectedRating, showInactiveOnly, sortOrder, setSearchParams]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-red-600">
          Error: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Manga Collection</h1>
          <div className="flex items-center gap-3">
            {isSelectMode ? (
              <>
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={selectedIds.size === 0}
                  className="inline-flex items-center justify-center h-10 px-4 rounded-full bg-red-600 text-white text-sm font-medium transition hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  完全に削除 {selectedIds.size > 0 && `(${selectedIds.size}件)`}
                </button>
                <button
                  type="button"
                  onClick={handleCancelSelectMode}
                  className="inline-flex items-center justify-center h-10 px-4 rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 text-sm font-medium transition"
                >
                  キャンセル
                </button>
              </>
            ) : (
              <>
                {showInactiveOnly && (
                  <button
                    type="button"
                    onClick={handleEnterSelectMode}
                    className="inline-flex items-center justify-center h-10 px-4 rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 text-sm font-medium transition"
                  >
                    選択して削除
                  </button>
                )}
                <Link
                  to="/history"
                  className="inline-flex items-center justify-center h-10 px-4 rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 text-sm font-medium transition"
                  title="閲覧履歴"
                >
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  履歴
                </Link>
            <button
              type="button"
              onClick={() => setIsFilterOpen((prev) => !prev)}
              className={`inline-flex items-center justify-center h-10 w-10 rounded-full border transition ${
                selectedTags.length > 0
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-gray-300 text-gray-500 bg-white hover:text-gray-700'
              }`}
              aria-label="Toggle tag filters"
              aria-pressed={isFilterOpen}
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="10" y1="18" x2="14" y2="18" />
              </svg>
            </button>
              </>
            )}
          </div>
        </div>

        {/* キーワード検索 */}
        <div className="mb-4">
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="タイトル・作者名で検索..."
            className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* 50音順フィルタ & レーティングフィルタ */}
        <AuthorFilter
          selectedCategory={selectedAuthorCategory}
          onCategoryChange={handleAuthorCategoryChange}
          selectedRating={selectedRating}
          onRatingChange={handleRatingChange}
          sortOrder={sortOrder}
          onSortCycle={handleSortCycle}
        />

        {isFilterOpen && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
              <span>Filters</span>
              <div className="flex items-center gap-3">
                <Link
                  to="/tags"
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  タグ管理
                </Link>
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5"
                    checked={showInactiveOnly}
                    onChange={(event) => setShowInactiveOnly(event.target.checked)}
                  />
                  inactiveのみ表示
                </label>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  すべて解除
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-4">
              <span>Tag filters:</span>
              {selectedTags.length === 0 && (
                <span className="text-gray-400">None selected</span>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {TAG_GROUPS.map((group) => (
                <div key={group.key}>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {group.label}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {groupedTags[group.key]?.map((tag) => {
                      const active = selectedTags.includes(tag.name);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTag(tag.name)}
                          className={`px-3 py-1 rounded-full border text-xs transition hover:opacity-80 ${getTagFilterClass(tag.name, active)}`}
                        >
                          {getTagLabel(tag.name)}
                        </button>
                      );
                    })}
                    {groupedTags[group.key]?.length === 0 && (
                      <span className="text-xs text-gray-400">No tags</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}

        {data && displayEntries.length === 0 && (
          <div className="text-center text-gray-500 mt-12">
            No manga found
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {displayEntries.map((entry) =>
            entry.type === 'series' ? (
              <SeriesCard
                key={entry.seriesKey}
                series={entry}
                onVolumeClick={handleCardClick}
                isSelectMode={isSelectMode}
                selectedIds={selectedIds}
                onSelect={handleToggleIds}
              />
            ) : (
              <MangaCard
                key={entry.item.id}
                item={entry.item}
                onClick={() => handleCardClick(entry.item.id)}
                isSelectMode={isSelectMode}
                isSelected={selectedIds.has(entry.item.id)}
                onSelect={handleToggleId}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
}
